import React, { useState, useEffect } from 'react';
import { X, Clock, Flame, Calendar, Share2, Trash2, Edit } from 'lucide-react';
import { BakingSession, FERMENTATION_PROCESSES } from '../../types/baking';
import { deleteBakingSession, updateBakingSession } from '../../lib/store';

interface ImageModalProps {
  imageUrl: string;
  title: string;
  session: BakingSession;
  onClose: () => void;
  onUpdate?: (sessionId: string, updates: Partial<BakingSession>) => void;
}

export function ImageModal({ imageUrl, title, session, onClose, onUpdate }: ImageModalProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState(session.result?.comments || '');
  const [error, setError] = useState<string | null>(null);

  // Update local state when session changes
  useEffect(() => {
    setEditedNotes(session.result?.comments || '');
  }, [session.result?.comments]);

  if (!session) return null;

  const fermentationProcess = FERMENTATION_PROCESSES.find(p => p.id === session.fermentationType);
  
  const formatDateTime = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleShareResults = () => {
    window.open('https://www.facebook.com/groups/1875669956357222/', '_blank', 'noopener,noreferrer');
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError(null);
      await deleteBakingSession(session.id);
      onClose();
    } catch (err) {
      console.error('Error deleting session:', err);
      setError('Failed to delete session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      const updates = {
        ...session,
        result: session.result ? {
          ...session.result, 
          comments: editedNotes,
        } : undefined
      };
      
      await updateBakingSession(session.id, {
        ...session,
        result: session.result ? {
          ...session.result,
          comments: editedNotes,
        } : undefined
      });
      
      // Update local state through parent component
      if (onUpdate) {
        onUpdate(session.id, updates);
      }
      
      setIsEditingNotes(false);
    } catch (err) {
      console.error('Error updating notes:', err);
      setError('Failed to update notes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg backdrop-blur-xl overflow-hidden">
          <div className="p-6 border-b border-white/10 flex justify-between items-center">
            <div>
              <h2 className="text-2xl text-white font-brand font-light italic">{title}</h2>
              <p className="text-sm text-white/70 mt-1">
                Completed {session.result?.completedAt && formatDateTime(session.result.completedAt)}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-6">
            {/* Left column - Image */}
            <div>
              <div className="relative rounded-xl overflow-hidden bg-black/20">
                <img
                  src={imageUrl}
                  alt={title}
                  className="w-full h-auto max-h-[400px] object-contain"
                  loading="lazy"
                />
              </div>
              
              {session.result?.comments && (
                <div className="mt-4 p-4 bg-white/10 rounded-xl relative">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-sm font-medium text-white/90">Baker's Notes</h3>
                    <button
                      onClick={() => {
                        setIsEditingNotes(!isEditingNotes);
                        setEditedNotes(session.result.comments || '');
                      }}
                      className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4 text-white/70 hover:text-white" />
                    </button>
                  </div>
                  {isEditingNotes ? (
                    <div className="space-y-3">
                      <textarea
                        value={editedNotes}
                        onChange={(e) => setEditedNotes(e.target.value)}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20 min-h-[100px] text-sm"
                        placeholder="Add your notes about this bake..."
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setIsEditingNotes(false);
                            setEditedNotes(session.result.comments || '');
                          }}
                          className="px-3 py-1 text-sm border border-white/20 rounded-lg hover:bg-white/10"
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveNotes}
                          disabled={loading || editedNotes === session.result.comments}
                          className="px-3 py-1 text-sm bg-amber-500/90 hover:bg-amber-500 text-white rounded-lg disabled:opacity-50"
                        >
                          {loading ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-white/80 whitespace-pre-line">
                      {session.result.comments}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Right column - Session Details */}
            <div className="space-y-4">
              {fermentationProcess && (
                <div className="p-4 bg-white/10 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-amber-400" />
                    <h3 className="font-medium text-white">Fermentation Process</h3>
                  </div>
                  <p className="text-white/90">{fermentationProcess.name}</p>
                  <p className="text-sm text-white/70 mt-1">{fermentationProcess.description}</p>
                </div>
              )}

              {/* Timing */}
              <div className="p-4 bg-white/10 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-amber-400" />
                  <h3 className="font-medium text-white">Timing</h3>
                </div>
                <div className="space-y-2 text-sm text-white/80">
                  <p>Target Bake Time: {formatDateTime(session.targetBakeTime)}</p>
                  {fermentationProcess && (
                    <>
                      <p>Total Time: {fermentationProcess.totalTime}h</p>
                      <p>Bulk Time: {fermentationProcess.bulkTime}h</p>
                      <p>Proof Time: {fermentationProcess.proofTime}h</p>
                    </>
                  )}
                </div>
              </div>

              {/* Oven Setup */}
              <div className="p-4 bg-white/10 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Flame className="w-5 h-5 text-amber-400" />
                  <h3 className="font-medium text-white">Oven Setup</h3>
                </div>
                <p className="text-white/90">
                  {session.ovenType === 'domestic' ? 'Domestic Oven' : 'Wood-Fired Oven'}
                </p>
                {session.woodOvenDetails && (
                  <div className="mt-2 text-sm text-white/70">
                    <p>Size: {session.woodOvenDetails.size}</p>
                    <p>Last used: {formatDateTime(session.woodOvenDetails.lastUsed)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer with Share Results button */}
          <div className="p-6 border-t border-white/10 bg-white/5">
            <div className="flex justify-between items-center">
              {showDeleteConfirm ? (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-6 py-2 border border-white/20 rounded-xl hover:bg-white/10 transition-colors"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={loading}
                    className="flex items-center gap-2 px-6 py-2 bg-red-500/90 hover:bg-red-500 text-white rounded-xl transition-all duration-300"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Confirm Delete
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-sm sm:text-base flex items-center gap-2 px-6 py-2 text-red-300 hover:text-red-200 hover:bg-red-500/10 rounded-xl transition-colors"
                  >
                    <Trash2 className="hidden sm:block w-4 h-4" />
                    Delete Result
                  </button>
                  <button
                    onClick={handleShareResults}
                    className="text-sm sm:text-base flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-500/90 to-blue-600/90 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl transition-all duration-300"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}